// Copyright © 2023 The Things Industries B.V.

import { isEmpty, isObject } from 'lodash'

import tts from '@console/api/tts'

import createRequestLogic from '@ttn-lw/lib/store/logics/create-request-logic'
import { isToday } from '@ttn-lw/lib/date-utils'

import * as noc from '@console/store/actions/network-operations-center.tti'

import {
  selectNocActiveApplications,
  selectNocActiveEndDevices,
  selectNocConnectedGateways,
} from '@console/store/selectors/network-operations-center.tti'

const getNocConfigurationLogic = createRequestLogic({
  type: noc.GET_NOC_CONFIGURATION,
  process: async () => tts.Noc.getConfiguration(),
})

const getNocConnectedGatewaysLogic = createRequestLogic({
  type: noc.GET_NOC_CONNECTED_GATEWAYS,
  process: async ({ action, getState }) => {
    const { from, to, bucketInterval } = action.payload
    const connectedGateways = selectNocConnectedGateways(getState())

    if (isEmpty(connectedGateways) || !isToday(connectedGateways.lastFetch)) {
      const data = await tts.Noc.getConnectedGateways(from, to, bucketInterval)

      return { data: data.daily_buckets, lastFetch: new Date() }
    }

    return connectedGateways
  },
})

const getNocActiveApplicationsLogic = createRequestLogic({
  type: noc.GET_NOC_ACTIVE_APPLICATIONS,
  process: async ({ action, getState }) => {
    const { from, to, bucketInterval } = action.payload
    const activeApplications = selectNocActiveApplications(getState())

    if (isEmpty(activeApplications) || !isToday(activeApplications.lastFetch)) {
      const data = await tts.Noc.getActiveApplications(from, to, bucketInterval)

      return { data: data.daily_buckets, lastFetch: new Date() }
    }

    return activeApplications
  },
})

const getNocActiveEndDevicesLogic = createRequestLogic({
  type: noc.GET_NOC_ACTIVE_END_DEVICES,
  process: async ({ action, getState }) => {
    const { from, to, bucketInterval } = action.payload
    const activeEndDevices = selectNocActiveEndDevices(getState())

    if (isEmpty(activeEndDevices) || !isToday(activeEndDevices.lastFetch)) {
      const data = await tts.Noc.getActiveEndDevices(from, to, bucketInterval)

      return { data: data.daily_buckets, lastFetch: new Date() }
    }

    return activeEndDevices
  },
})

const getNocGatewayUptimeLogic = createRequestLogic({
  type: noc.GET_NOC_GATEWAY_UPTIME,
  process: async ({ action }) => {
    const { gatewayId, from, to } = action.payload

    const data = await tts.Noc.getGatewayUptime(gatewayId, from, to)

    return { gatewayId, uptime: isObject(data) ? data.data : data }
  },
})

const getGatewayPacketsPerDataRateLogic = createRequestLogic({
  type: noc.GET_GATEWAY_PACKETS_PER_DATA_RATE,
  process: async ({ action }) => {
    const { gatewayId, from, to, bucketInterval } = action.payload

    const data = await tts.Noc.getGatewayPacketsPerDataRate(gatewayId, from, to, bucketInterval)

    return data?.data_rates ?? []
  },
})

const getGatewayPacketsPerChannelLogic = createRequestLogic({
  type: noc.GET_GATEWAY_PACKETS_PER_CHANNEL,
  process: async ({ action }) => {
    const { gatewayId, from, to, bucketInterval } = action.payload

    const data = await tts.Noc.getGatewayPacketsPerChannel(gatewayId, from, to, bucketInterval)

    return data?.channels ?? []
  },
})

const getApplicationPacketsPerDataRateLogic = createRequestLogic({
  type: noc.GET_APPLICATION_PACKETS_PER_DATA_RATE,
  process: async ({ action }) => {
    const { applicationId, from, to, bucketInterval } = action.payload

    const data = await tts.Noc.getApplicationPacketsPerDataRate(
      applicationId,
      from,
      to,
      bucketInterval,
    )

    return data?.data_rates ?? []
  },
})

const getApplicationPacketsPerChannelLogic = createRequestLogic({
  type: noc.GET_APPLICATION_PACKETS_PER_CHANNEL,
  process: async ({ action }) => {
    const { applicationId, from, to, bucketInterval } = action.payload

    const data = await tts.Noc.getApplicationPacketsPerChannel(
      applicationId,
      from,
      to,
      bucketInterval,
    )

    return data?.channels ?? []
  },
})

const getEndDevicePacketsPerDataRateLogic = createRequestLogic({
  type: noc.GET_END_DEVICE_PACKETS_PER_DATA_RATE,
  process: async ({ action }) => {
    const { applicationId, deviceId, from, to, bucketInterval } = action.payload

    const data = await tts.Noc.getEndDevicePacketsPerDataRate(
      applicationId,
      deviceId,
      from,
      to,
      bucketInterval,
    )

    return data?.data_rates ?? []
  },
})

const getEndDevicePacketsPerChannelLogic = createRequestLogic({
  type: noc.GET_END_DEVICE_PACKETS_PER_CHANNEL,
  process: async ({ action }) => {
    const { applicationId, deviceId, from, to, bucketInterval } = action.payload

    const data = await tts.Noc.getEndDevicePacketsPerChannel(
      applicationId,
      deviceId,
      from,
      to,
      bucketInterval,
    )

    return data?.channels ?? []
  },
})

export default [
  getNocConfigurationLogic,
  getNocConnectedGatewaysLogic,
  getNocActiveApplicationsLogic,
  getNocActiveEndDevicesLogic,
  getNocGatewayUptimeLogic,
  getGatewayPacketsPerDataRateLogic,
  getGatewayPacketsPerChannelLogic,
  getApplicationPacketsPerDataRateLogic,
  getApplicationPacketsPerChannelLogic,
  getEndDevicePacketsPerDataRateLogic,
  getEndDevicePacketsPerChannelLogic,
]
